import { BehaviorSubject } from 'rxjs';

import { FieldEditChangedArgs, FieldMetadata, FieldWidthEnum } from 'src/cer/cer-data/cer-data-types';
import { CerGridComponent } from 'src/cer/cer-grid/cer-grid.component';
import { UiActionTypeEnum, UiCommand, UiCommandEvent, UiCommandSourceEnum } from '@ui/cer-ui-types';
import { CerFormView, ICerViewComponent } from '@form/cer-form-view';
import { CerFormViewSet } from '@form/cer-form-view-set';
import { CerFormAdapter } from '@form/cer-form-adapter';

import { CerFormPanesSettings } from '@form/cer-form-panes/cer-form-panes.service';

import { ProjectTaskService } from './project-task.service';
import { CerUiIcons } from '@ui/cer-ui-icons';
import { CerGridEditService } from 'src/cer/cer-grid/cer-grid-edit.service';
import { ProductPartyRoleForm } from '@app/product/forms/product-party-role.form';
import { uiCommandAddSelectedToProductRole } from '@app/party/party.form';
import { ProductPartyRoleClient } from '@app/api';

export class ProjectTaskForm extends CerFormAdapter {

  private projectTaskService: ProjectTaskService;

  private taskMode: boolean = false;
  private contactMode: boolean = false;
  private params: string[] = null;

  private uiCmdTaskContactsCreate: UiCommand = { id: 'taskContactsCreate', text: 'Opret kontakter', tooltipText: 'Opret kontakter', iconCss: CerUiIcons.location, target: ".e-content" };
  private uiCmdTaskClose: UiCommand = { id: 'taskClose', text: 'Afslut', tooltipText: 'Afslut opgave', iconCss: CerUiIcons.checkMark, target: ".e-content", needSelection: true };

  override viewSetInit(viewSet: CerFormViewSet): void {
    this.projectTaskService = this.routeOptions.context.injector.get(ProjectTaskService);

    const routeData: any = this.routeOptions.routeData;

    this.taskMode = (routeData?.type == 'task');
    this.params = this.routeOptions.routeParams;
    this.contactMode = this.taskMode && (this.params != null && this.params.length > 0);

    // Panes
    viewSet.panes = this.panesGet();

    // Views
    const taskView = this.taskViewGet();
    taskView.paneTarget = 'main';

    if (this.taskMode) {
      viewSet.viewAdd(taskView);

      const contactView = this.contactViewGet();
      contactView.paneTarget = 'bottom';
      viewSet.viewAdd(contactView, taskView);

      const reviewerRoleView = this.reviewerRoleViewGet();
      reviewerRoleView.paneTarget = 'bottom';
      viewSet.viewAdd(reviewerRoleView, taskView);
    }
    else {
      const projectView = this.projectViewGet();
      projectView.paneTarget = 'top';
      viewSet.viewAdd(projectView);
      taskView.viewMetadata.parentPrimaryKey = 'id';
      taskView.viewMetadata.parentForeignKey = 'projectNode';
      taskView.viewMetadata.dataApiParams = ['$childTasks'];
      viewSet.viewAdd(taskView, projectView);
    }
  }

  private reviewerRoleView: CerFormView;
  private reviewerRoleViewGet() {
    this.reviewerRoleView = ProductPartyRoleForm.productPartyRoleViewGet(true, true, true, this.context);
    this.reviewerRoleView.title = 'Tilknyttede anmeldere';
    this.reviewerRoleView.viewMetadata.text = this.reviewerRoleView.title;
    this.reviewerRoleView.viewMetadata.parentForeignKey = 'product';
    this.reviewerRoleView.viewMetadata.parentPrimaryKey = 'product';
    return this.reviewerRoleView;
  }

  private reviewerRoleViewResearch() {
    this.reviewerRoleView?.research();
  }

  private panesGet(): CerFormPanesSettings {
    return {
      top: { visible: this.taskMode ? 'hide' : 'show', size: '30%' },
      main: { visible: 'show', size: this.taskMode ? '70%' : '50%', sizeSecondary: '60%' },
      right: { visible: 'collapse', size: '40%' },  // show -> add view containing FreeText
      bottom: { visible: this.contactMode ? 'show' : 'collapse', size: this.taskMode ? '30%' : '20%' },
      orientationPrimary: 'vertical'
    };
  }

  //#region Project view
  private projectViewGet(): CerFormView {
    // Project View
    var view = new CerFormView();

    view.onCommandEvent = (e => this.onProjectCommand(e));
    view.viewMetadata = {
      name: 'project-node', dataApiName: 'ProjectNode', text: 'Projekt struktur',
      primaryKey: 'id', titleFields: ['name'], allowEdit: true, allowCreate: true, allowDelete: true, baseFields: ['history']
    };
    if (this.params?.length > 0) {
      view.viewMetadata.dataApiParams = this.params;
      view.viewMetadata.allowCreate = false;
      view.viewMetadata.allowEdit = false;
      view.viewMetadata.allowDelete = false;
    }

    view.fieldMetadata = [
      { name: 'id', text: 'Id', tooltipText: 'Unik nøgle for skabelonen', allowEdit: false, visible: false, widthType: FieldWidthEnum.XS },
      { name: 'level', text: 'Niveau', tooltipText: 'Projektniveau', widthType: FieldWidthEnum.XXS, foreignKeyTableName: 'ProjectLevel', required: true, orderBy: 'Ascending', visible: false, visibleAdd: true, visibleEdit: true, orderByPriority: 1 },
      { name: 'parentId', text: 'Overordnet struktur', tooltipText: 'Overordnet projektstruktur', widthType: FieldWidthEnum.M, foreignKeyTableName: 'ProjectNode', orderByPriority: 2 },
      { name: 'num', text: 'Projektnr.', tooltipText: 'Projektnummer', widthType: FieldWidthEnum.M, visible: false },
      { name: 'name', text: 'Navn', tooltipText: 'Projektnavn', widthType: FieldWidthEnum.M, required: true, orderByPriority: 3 },
      { name: 'responsible', text: 'Ansvarlig', tooltipText: 'Ansvarlig medarbejder for projekt', widthType: FieldWidthEnum.M, foreignKeyTableName: 'User', foreignKeyField: 'shortName', foreignKeyValue: 'shortName' },
      { name: 'taskType', text: 'Opgavetype', tooltipText: 'Type af tilknyttede opgaver', widthType: FieldWidthEnum.M, foreignKeyTableName: 'ProjectTaskType', foreignKeyField: 'num', visible: false, visibleAdd: true, visibleEdit: true },
      { name: 'taskStatus', text: 'Opgavestatus', tooltipText: 'Status omkring opgaveløsning', widthType: FieldWidthEnum.M, foreignKeyTableName: 'ProjectTaskStatus', foreignKeyField: 'num', required: true },
      { name: 'taskResponsibleTeam', text: 'Opgave team', tooltipText: 'Ansvarligt team for opgaver', widthType: FieldWidthEnum.M, foreignKeyTableName: 'Team', foreignKeyField: 'num', visible: false, visibleAdd: true, visibleEdit: true },
      { name: 'taskResponsibleUser', text: 'Opgave ansvarlig', tooltipText: 'Ansvarlig medarbejder for opgaver', widthType: FieldWidthEnum.M, foreignKeyTableName: 'User', foreignKeyField: 'shortName', foreignKeyValue: 'shortName', visible: false, visibleAdd: true, visibleEdit: true }
    ];

    return view;
  }

  private onProjectCommand(event: UiCommandEvent) {
    //this.projectTaskService.setTaskGrid(this.taskGrid);
    if (this.contactMode) {
      this.checkContactVisibility(event, 'taskType');
    }
  }
  //#endregion

  //#region Task view
  private taskViewGet(): CerFormView {

    var view = new CerFormView();

    view.viewMetadata = {
      name: 'project-task', dataApiName: 'ProjectTask', text: 'Opgaver', textSingular: 'Opgave',
      primaryKey: 'id', titleFields: ['name'],
      allowEdit: true, allowCreate: true, allowDelete: true, baseFields: ['history']
    };
    if (this.taskMode && this.params?.length > 0) {
      view.viewMetadata.dataApiParams = this.params;
    }

    view.fieldMetadata = [
      { name: 'id', text: 'Id', tooltipText: 'Unik nøgle for skabelonen', allowEdit: false, visible: false, widthType: FieldWidthEnum.XS, orderBy: 'Descending', orderByPriority: 2 },
      { name: 'projectNode', text: 'Projekt', tooltipText: 'Projekt', widthType: FieldWidthEnum.M, foreignKeyTableName: 'ProjectNode', onEditChanged: (args) => { this.onProjectNodeChanged(args) } },
      { name: 'type', text: 'Type', tooltipText: 'Opgavetype', widthType: FieldWidthEnum.M, foreignKeyTableName: 'ProjectTaskType', foreignKeyField: 'num', onEditChanged: (args) => { this.onTaskTypeChanged(args) } },
      {
        fieldType: 'product', name: 'product', text: 'Udgivelse', tooltipText: 'Udgivelse', widthType: FieldWidthEnum.M, onEditChanged: (args) => { this.onProductChanged(args) }
      },
      { name: 'name', text: 'Navn', tooltipText: 'Overskrift', widthType: FieldWidthEnum.M, orderBy: 'Ascending' },
      { name: 'status', text: 'Status', tooltipText: 'Opgavestatus', widthType: FieldWidthEnum.M, foreignKeyTableName: 'ProjectTaskStatus', foreignKeyField: 'num', foreignKeySortFields: ['id'], orderBy: 'Descending', orderByPriority: 1 },
      { name: 'responsibleTeam', text: 'Team', tooltipText: 'Ansvarligt team', widthType: FieldWidthEnum.M, foreignKeyTableName: 'Team', foreignKeyField: 'num' },
      { name: 'responsibleUser', text: 'Ansvarlig', tooltipText: 'Ansvarlig medarbejder', widthType: FieldWidthEnum.M, foreignKeyTableName: 'User', foreignKeyField: 'shortName', foreignKeyValue: 'shortName' },
      { name: 'description', text: 'Beskrivelse', tooltipText: 'Beskrivelse', format: 'TextArea', widthType: FieldWidthEnum.M, visible: false }
    ];

    view.fieldGroupMetadata = [
      { idx: 1, tabIdx: 0, name: 'identification', text: 'Opgave', fields: ['id', 'productNode', 'type', 'product', 'name'] },
      { idx: 2, tabIdx: 0, name: 'status', text: 'Status', fields: ['status', 'responsibleTeam', 'responsibleUser'] }
    ];

    view.toolbarCommands = [this.uiCmdTaskClose];
    view.contextMenuCommands = [this.uiCmdTaskClose, this.uiCmdTaskContactsCreate];;
    view.onCommandEvent = (e => this.onTaskCommand(e));
    return view;
  }

  private onTaskCommand(event: UiCommandEvent) {

    if (this.taskMode) {
      this.checkContactVisibility(event, 'type');
    }
    if (event.commandId == this.uiCmdTaskClose.id) {
      this.projectTaskService.taskClose(event.rowData, event.appComponent);
    }
    if (event.commandId == uiCommandAddSelectedToProductRole.id) {
      this.reviewersAdd(event.appComponent, event.rowsData);
    }

    if (event.source == UiCommandSourceEnum.ActionComplete) {
      switch (event.actionType) {
        case UiActionTypeEnum.Add:
          this.projectNumChanged(event);
          break;
        case UiActionTypeEnum.RowSelect:
          //var description: string = event.rowData?.description ? event.rowData.description : '';
          //description$.next(description);
          break;
      }
    }
  }

  private reviewersAdd(c: ICerViewComponent, partyNums: string[]) {
    if (partyNums == null || partyNums.length == 0) {
      this.context.ui.error("Ingen anmelder er markeret");
      return;
    }
    const product: string = c.rowSelectedData()?.product;
    if (product == null || product.length == 0) {
      this.context.ui.error("Ingen udgivelse er markeret");
      return;
    }
    this.productReviewersAdd(c, product, partyNums);
  }

  private productReviewersAdd(c: ICerViewComponent, product: string, partyNums: string[]) {
    const client = this.context.injector.get(ProductPartyRoleClient);

    client.addParty(product, partyNums).subscribe(
      {
        next: (count: number) => {
          this.context.ui.toast(`${count} anmelder(e) er tilknyttet udgivelse`);
          this.reviewerRoleViewResearch();
          c.rowSelectedRefresh();
        },
        error: (error) => {
          this.context.ui.errorApi(error);
        }
      }
    );
  }

  private projectNumChanged(event: UiCommandEvent) {
    //const fieldName = 'projectNode';
    const rowData = event.appComponent.rowSelectedData() as any; // Copy from previous selection when adding
    const value = rowData?.projectNode;
    if (value) {
      const c = event.appComponent;
      const e = c.editService;
      if (!e.fieldValueGet('projectNode')) {
        e.fieldValueSet('projectNode', value);
      }
      this.initFromProjectNode(value, e, c.fieldMetadata);
    }
  }

  // During editing, when "Product.RequestContractCreate" changes, update "Contract.Type"
  private onProjectNodeChanged(args: FieldEditChangedArgs) {
    this.initFromProjectNode(args.value, args.editService, args.fieldsMeta)
  }

  private initFromProjectNode(value: any, e: CerGridEditService, fieldsMeta: FieldMetadata[]) {
    if (value) {
      if (e.fieldValueGet('type') == null) {
        var fieldMeta: FieldMetadata = fieldsMeta.find(f => f.name == 'projectNode');
        if (fieldMeta) {
          if (!e.fieldValueGet('projectNode')) {
            e.fieldValueSet('projectNode', value);
          }
          var fkData = fieldMeta.foreignKey?.data.find(f => f.id == value);
          if (fkData) {

            if (fkData.taskType) {

              e.fieldValueSet('type', fkData.taskType);
              this.initFromTaskType(fkData.taskType, fieldsMeta, e);
            }
          }
        }
      }
    }
  }

  private onProductChanged(args: FieldEditChangedArgs) {
    if (args.value != null && args.value != undefined) {
      var e = args.editService;
      const name  = (args.itemData.name + ' ' + (args.itemData.authors ?? '')).trim();
      e.fieldValueSet('name', name);
    }
  }

  private onTaskTypeChanged(args: FieldEditChangedArgs) {
    this.initFromTaskType(args.value, args.fieldsMeta, args.editService);
  }

  private initFromTaskType(value: any, fieldsMeta: FieldMetadata[], e: CerGridEditService) {
    if (value) {
      var fieldMeta: FieldMetadata = fieldsMeta.find(f => f.name == 'type');
      var fkData = fieldMeta.foreignKey?.data.find(f => f.num == value);
      if (fkData) {
        if (!e.fieldValueGet('status') && fkData.status) {
          e.fieldValueSet('status', fkData.status);
        }
        if (e.fieldValueGet('responsibleTeam') == null) {
          e.fieldValueSet('responsibleTeam', fkData.responsibleTeam);
        }
        if (e.fieldValueGet('responsibleUser') == null) {
          e.fieldValueSet('responsibleUser', fkData.responsibleUser);
        }
        e.fieldDisable('product', !fkData.product);
      }
    }
  }

  //#endregion

  //#region Contact view
  private contactViewGet(): CerFormView {
    var view = new CerFormView();

    view.listEditSettings = { mode: 'default', deleteConfirm: false }
    view.viewMetadata = {
      name: 'project-task-contact', dataApiName: 'ProjectTaskContact', text: 'Kontakter',
      parentPrimaryKey: 'id', parentForeignKey: 'task',
      primaryKey: 'id', titleFields: ['name'],
      allowEdit: true, allowCreate: true, allowDelete: true,
      baseFields: ['history']
    };
    view.fieldMetadata = [
      { name: 'id', text: 'Id', tooltipText: 'Unik nøgle for skabelonen', allowEdit: false, visible: false, widthType: FieldWidthEnum.XS },
      { name: 'task', text: 'Opgave', tooltipText: 'Opgavereference', widthType: FieldWidthEnum.M, foreignKeyTableName: 'ProjectTask', visible: false },
      { name: 'partyRole', text: 'Rolle', tooltipText: 'Kontaktens rolles', widthType: FieldWidthEnum.M, foreignKeyTableName: 'PartyRole', foreignKeyField: 'num' },
      { name: 'name', text: 'Navn', tooltipText: 'Navn på kontakt', widthType: FieldWidthEnum.XL, orderBy: 'Ascending' },
      { name: 'party', text: 'Part', tooltipText: 'Part informations', widthType: FieldWidthEnum.XL, visible: false },
      { name: 'description', text: 'Beskrivelse', tooltipText: 'Beskrivelse', widthType: FieldWidthEnum.XXL, visible: true },
      { name: 'status', text: 'Status', tooltipText: 'Opgavestatus', widthType: FieldWidthEnum.M, foreignKeyTableName: 'ProjectTaskStatus', foreignKeyField: 'num', foreignKeySortFields: ['id'], visible: false },
      { name: 'responsibleTeam', text: 'Team', tooltipText: 'Ansvarligt team', widthType: FieldWidthEnum.M, foreignKeyTableName: 'Team', foreignKeyField: 'num', visible: false },
      { name: 'responsibleUser', text: 'Ansvarlig', tooltipText: 'Ansvarlig medarbejder', widthType: FieldWidthEnum.M, foreignKeyTableName: 'User', foreignKeyField: 'shortName', visible: false }
    ];
    view.toolbarCommands = [this.uiCmdTaskContactsCreate];
    view.onCommandEvent = (e => this.onContactCommand(e));
    return view;
  }

  private checkContactVisibility(event: UiCommandEvent, fieldName: string) {
    if (event.source == UiCommandSourceEnum.ActionComplete && event.actionType == 'dataBound') {
      if (this.context.form?.panes?.bottom?.visible != 'show') {
        var hasContact = this.hasContactProjectTaskType(event.rowsData, fieldName, event.appComponent?.fieldMetadata);
        if (hasContact) {
          this.context.form.panesService.visibleSet('bottom', 'show');
        }
      }
    }
  }

  private hasContactProjectTaskType(rowsData: any[], fieldName: string, fieldMetas: FieldMetadata[]) {
    var hasContact = false;
    if (rowsData?.length > 0 && fieldMetas?.length > 0) {
      const fields: FieldMetadata[] = fieldMetas.filter(field => field.name == fieldName);
      if (fields?.length > 0) {
        var field: FieldMetadata = fields[0];
        var types: any[] = field.foreignKey?.data.filter(rowType => rowType.productRoles == true);
        if (types && types.length > 0) {
          rowsData.forEach(row => {
            types.forEach(t => {
              if (t.num == row[fieldName]) {
                hasContact = true;
              }
            });
          });
        }
      }
    }
    return hasContact;
  }

  private contactComponent: CerGridComponent;
  private onContactCommand(event: UiCommandEvent) {
    if (this.contactComponent == null && event.appComponent != null) {
      this.contactComponent = event.appComponent as CerGridComponent;
    }
    if (event.commandId == this.uiCmdTaskContactsCreate.id) {
      this.projectTaskService.taskContactsCreate(event.appComponent.parentRowData(), this.contactComponent);
    }
  }
  //#endregion

  //#region Desciption
  private description$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  protected editDescription() {
    //this.projectTaskService.editDescription(this.taskComponent, this.description$);
  }
  //#endregion

}

